import { Controller } from "@hotwired/stimulus"

// Activates the `.Animation` elements inside it.
//
// Each Animate element starts in its "before" state and moves when it gains
// `Animation--active`. This controller decides when: when it scrolls into
// view (the default), when it is clicked, or immediately. Children are
// staggered by a fixed increment so a list cascades instead of popping.
//
// Reduced motion: everything is activated at once with transitions turned
// off. The content must never stay hidden just because someone turned
// animations off.
export default class extends Controller {
  static values = {
    trigger: { type: String, default: "on-visible" },
    visibility: { type: String, default: "bottom-of-screen" },
    runOnce: { type: Boolean, default: false },
    stagger: { type: Number, default: 100 },
    disabled: { type: Boolean, default: false }
  }

  connect() {
    this.elements = Array.from(this.element.querySelectorAll(".Animation"))
    if (this.elements.length === 0) return

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (this.disabledValue || reducedMotion) {
      for (const element of this.elements) {
        element.style.transition = "none"
        element.classList.add("Animation--active")
      }
      return
    }

    if (this.staggerValue > 0) {
      this.elements.forEach((element, i) => {
        element.style.transitionDelay = `${i * this.staggerValue}ms`
      })
    }

    switch (this.triggerValue) {
      case "immediate":
        requestAnimationFrame(() => {
          for (const element of this.elements) element.classList.add("Animation--active")
        })
        break
      case "click":
        this.clickHandler = event => {
          if (event.target.classList.contains("Animation")) event.target.classList.add("Animation--active")
        }
        this.element.addEventListener("click", this.clickHandler)
        break
      default:
        this.observer = new IntersectionObserver(entries => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add("Animation--active")
              if (this.runOnceValue) this.observer.unobserve(entry.target)
            } else if (!this.runOnceValue) {
              entry.target.classList.remove("Animation--active")
            }
          }
        }, this.observerOptions())
        for (const element of this.elements) this.observer.observe(element)
    }
  }

  disconnect() {
    this.observer?.disconnect()
    if (this.clickHandler) this.element.removeEventListener("click", this.clickHandler)
  }

  observerOptions() {
    switch (this.visibilityValue) {
      case "bottom-of-screen": return { rootMargin: "0px 0px -10% 0px", threshold: 0.4 }
      case "middle-of-screen": return { rootMargin: "0px 0px -30% 0px", threshold: 0.4 }
      case "about-to-leave": return { rootMargin: "0px 0px -100% 0px", threshold: 0.4 }
      default: {
        // A number is a percentage of the element that must be visible.
        const percent = Number(this.visibilityValue)
        return { rootMargin: "0px", threshold: Number.isFinite(percent) ? percent / 100 : 0.4 }
      }
    }
  }
}
